/* eslint-disable react-hooks/exhaustive-deps */
import { ButtonPrimary } from '@/components/button'
import { AddCircle, Close, RemoveCircle, UserIconFilled } from '@/components/icons'
import { DatePickerInput } from '@/components/input'
import { MAX_GAMES } from '@/constants/Common'
import { useAppSelector } from '@/hooks/useRedux'
import { BracketMatch } from '@/types'
import { UpdateScoreSchema } from '@/utils/schema/AddMatchValidationSchema'
import { Dialog, Transition } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import moment from 'moment'
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { eventSelector } from '../selectors'
import { ConfirmAddEditScoreModal } from './ConfirmAddEditScoreModal'
type Props = {
  open: boolean
  onClose: () => void
  match: BracketMatch
  title: string
  handleOnSubmit: (data: any, flag?: boolean) => void
  director: boolean
}

export const AddEditScoresModal = ({ open, onClose, match, title, handleOnSubmit, director }: Props) => {
  const { t } = useTranslation()

  const { bracket } = useAppSelector(eventSelector)

  const [showWarningPopup, setShowWarningPopup] = useState(false)
  const [showCompletedErrorPopup, setCompletedErrorPopup] = useState(false)
  const [showOngoingErrorPopup, setOngoingErrorPopup] = useState(false)
  const [currentWinner, setCurrentWinner] = useState('')
  const [scoreErrors, setScoreErrors] = useState([])

  const { teams, noOfGames } = match
  const compStartDate = new Date(moment?.tz(bracket?.durationDateTime?.[0], bracket?.timeZone as any) as any)
  const compEndDate = new Date(moment?.tz(bracket?.durationDateTime?.[1], bracket?.timeZone as any) as any)
  const appendValues = {
    team1: '',
    team2: '',
  }

  const defaultValues = {
    matchDate: '',
    games: [appendValues],
    winner: '',
    minDate: compStartDate,
    maxDate: compEndDate,
  }

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    getValues,
    clearErrors,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: useMemo(() => {
      return defaultValues
    }, [defaultValues]),
    resolver: yupResolver(UpdateScoreSchema as any),
    mode: 'onSubmit',
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'games',
  })

  const watchGames = watch('games')

  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchGames[index],
    }
  })

  const handleClose = () => {
    reset(defaultValues)
    onClose()
  }
  const onAppend = () => {
    append(appendValues)
  }

  const onSubmit = (data: any) => {
    const totalGames = getValues('games').length
    if (bracket?.displayStatus === 'Ongoing' && match?.leagueMatchStatus === 'CONFIRMED') {
      setOngoingErrorPopup((prev) => !prev)
    } else if (bracket?.displayStatus === 'Completed' && match?.leagueMatchStatus === 'CONFIRMED') {
      setCompletedErrorPopup((prev) => !prev)
    } else if (totalGames === 1 || totalGames === 2 || totalGames === 3 || totalGames === 4) {
      setShowWarningPopup((prev) => !prev)
    } else {
      handleClose()
      handleOnSubmit(data)
    }
  }

  const handleEditedScore = (flag: any) => {
    const data = getValues()
    if (flag) {
      handleOnSubmit(data, true)
    } else {
      handleOnSubmit(data)
    }
    handleClose()
  }

  const resetWinner = (checkWinner: any, winner: any) => {
    if (checkWinner) {
      setCurrentWinner(winner)
    } else {
      setCurrentWinner('tie')
    }
  }

  useEffect(() => {
    if (isValid) {
      clearErrors()
    }
  }, [isValid])

  useEffect(() => {
    if (title === 'Edit Scores') {
      setValue('matchDate', match?.matchDate)
      for (let i = 1; i < noOfGames - 1; i += 1) {
        append({
          team1: match?.teams?.[0][`game${i as 1 | 2 | 3 | 4 | 5}`].toString(),
          team2: match?.teams?.[1][`game${i as 1 | 2 | 3 | 4 | 5}`].toString(),
        })
      }
      setValue(
        'games',
        [...Array(noOfGames)].map((_, id) => {
          const gameNumber = id + 1
          return {
            team1: match?.teams?.[0]?.[`game${gameNumber as 1 | 2 | 3 | 4 | 5}`].toString(),
            team2: match?.teams?.[1]?.[`game${gameNumber as 1 | 2 | 3 | 4 | 5}`].toString(),
          }
        })
      )
    }
  }, [open, title])

  useEffect(() => {
    let team1 = 0
    let team2 = 0
    let checkWinner = false
    controlledFields.forEach((game) => {
      if (parseInt(game?.team1, 10) > parseInt(game?.team2, 10)) {
        team1 += 1
      }
      if (parseInt(game?.team1, 10) < parseInt(game?.team2, 10)) {
        team2 += 1
      }

      if (!checkWinner && game?.team1 && game?.team2) {
        checkWinner = true
      }
    })
    if (team1 > team2) {
      resetWinner(checkWinner, 'team1')
      setValue('winner', 'team1')
    } else if (team1 < team2) {
      resetWinner(checkWinner, 'team2')
      setValue('winner', 'team2')
    } else {
      setCurrentWinner('tie')
      setValue('winner', 'tie')
    }
  }, [controlledFields])

  useEffect(() => {
    const array: any = []
    if (!errors?.games?.message && errors?.games?.length) {
      ;(errors as any)?.games?.forEach((c: any) => {
        if (!array.includes(c?.team1?.message)) {
          array.push(c?.team1?.message)
        }
        if (!array.includes(c?.team2?.message)) {
          array.push(c?.team2?.message)
        }
      })
    }
    setScoreErrors(array)
  }, [errors])

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full flex flex-col gap-y-6 max-w-2xl transform rounded-2xl bg-white px-6 py-4 text-left align-middle shadow-xl transition-all relative">
                <button
                  className="w-9 h-9 rounded-full bg-navy-900 bg-opacity-10 flex items-center justify-center ml-2 hover:bg-opacity-20 absolute right-2 top-2"
                  onClick={handleClose}
                >
                  <Close className="text-navy-900" />
                </button>

                <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold">
                  {title}
                </Dialog.Title>

                {new Date() < moment(bracket?.duration?.[0]).toDate() && !director ? (
                  <p className="text-boldOrange text-sm font-semibold">
                    {t('event.youCanAddScores')} {moment(bracket?.duration?.[0])?.format('MMMM DD')} -{' '}
                    {moment(bracket?.duration?.[1])?.format('MMMM DD, YYYY')}
                  </p>
                ) : null}

                <div className="flex flex-col gap-y-4">
                  <DatePickerInput
                    label={t('common.matchDate')}
                    labelClassName="!text-navy-900"
                    selected={watch('matchDate')}
                    timeInputLabel={`${t('common.time')}:`}
                    onChange={(date: Date | null) => {
                      date &&
                        setValue('matchDate', moment(date).format('YYYY-MM-DD'), {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                    }}
                    readOnly={new Date() < compStartDate}
                    minDate={compStartDate}
                    maxDate={new Date() > compEndDate ? compEndDate : new Date()}
                    error={errors.matchDate?.message}
                  />
                  <div className="flex flex-col gap-y-4">
                    <div className="flex justify-between gap-x-10 relative">
                      <div
                        className={`flex z-10 gap-x-4 items-center border flex-1 rounded p-4 bg-gray-300 ${
                          currentWinner === 'team1' ? 'border-green-400' : 'border-gray-400'
                        }`}
                      >
                        {teams?.[0]?.player1?.imageUrl ? (
                          <img src={teams?.[0]?.player1?.imageUrl} className="w-6 h-6 rounded-full" alt="" />
                        ) : (
                          <UserIconFilled className="w-6 h-6 text-primary-500" />
                        )}
                        <span className={`text-sm font-medium text-navy-900`}>{teams?.[0]?.player1?.fullName}</span>
                      </div>
                      {teams?.[0]?.player2 && (
                        <div
                          className={`flex gap-x-4 items-center border flex-1 rounded p-4 bg-gray-300 ${
                            currentWinner === 'team1' ? 'border-green-400' : 'border-gray-400'
                          }`}
                        >
                          {teams?.[0]?.player2?.imageUrl ? (
                            <img src={teams?.[0]?.player2?.imageUrl} className="w-6 h-6 rounded-full" alt="" />
                          ) : (
                            <UserIconFilled className="w-6 h-6 text-primary-500" />
                          )}
                          <span className={`text-sm font-medium text-navy-900`}>{teams?.[0]?.player2?.fullName}</span>
                        </div>
                      )}
                      <div
                        className={`absolute z-0 flex items-center w-full px-10 gap-x-2 ${
                          fields.length < 5 ? '-bottom-[120px]' : '-bottom-[110px]'
                        }`}
                      >
                        <div className="h-[1px] w-full bg-gray-300" />
                        {fields.length < MAX_GAMES && (
                          <button onClick={onAppend}>
                            <AddCircle className="text-primary" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-center gap-x-6">
                      {controlledFields.map((field, index) => {
                        return (
                          <div key={field.id} className="flex flex-col gap-y-2 items-center">
                            <div
                              className="bg-gray-300 text-xs rounded-full px-2 py-[2px] cursor-pointer text-navy-900 text-opacity-50 font-medium flex gap-x-1"
                              onClick={() => {
                                if (fields.length !== 1) remove(index)
                              }}
                            >
                              {t('common.game')} {index + 1}
                              {fields.length !== 1 && <RemoveCircle className="w-4 h-4" />}
                            </div>
                            <div className="flex flex-col gap-y-8 items-center justify-center">
                              <input
                                className={`bg-white text-navy-900 rounded w-12 h-12 text-sm text-center focus:outline-none border ${
                                  errors?.games?.[index]?.team1 && 'border-boldOrange'
                                }`}
                                maxLength={2}
                                value={watch(`games.${index}.team1`)}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const letterRegex = /^[0-9]*$/
                                  if (e.target.value.match(letterRegex)) {
                                    setValue(`games.${index}.team1`, e.target.value, {
                                      shouldDirty: true,
                                      shouldValidate: true,
                                    })
                                  }
                                }}
                              />
                              <input
                                className={`bg-white text-navy-900 rounded w-12 h-12 text-sm text-center focus:outline-none border ${
                                  errors?.games?.[index]?.team2 && 'border-boldOrange'
                                }`}
                                maxLength={2}
                                value={watch(`games.${index}.team2`)}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const letterRegex = /^[0-9]*$/
                                  if (e.target.value.match(letterRegex)) {
                                    setValue(`games.${index}.team2`, e.target.value, {
                                      shouldDirty: true,
                                      shouldValidate: true,
                                    })
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )
                      })}
                    </div>

                    <div
                      className={`text-boldOrange text-sm font-medium ${
                        errors?.games?.message ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      {errors?.games?.message ? errors?.games?.message : 'errors'}
                    </div>

                    {scoreErrors.map((field, index) => {
                      return (
                        <React.Fragment key={`${index + 1}`}>
                          <p className={`text-boldOrange text-sm font-medium `}>{field}</p>
                        </React.Fragment>
                      )
                    })}

                    <div className="flex justify-between gap-x-10">
                      <div
                        className={`flex gap-x-4 items-center border flex-1 rounded p-4 bg-gray-300 ${
                          currentWinner === 'team2' ? 'border-green-400' : 'border-gray-400'
                        }`}
                      >
                        {teams?.[1]?.player1?.imageUrl ? (
                          <img src={teams?.[1]?.player1?.imageUrl} className="w-6 h-6 rounded-full" alt="" />
                        ) : (
                          <UserIconFilled className="w-6 h-6 text-primary-500" />
                        )}
                        <span className={`text-sm font-medium text-navy-900`}>{teams?.[1]?.player1?.fullName}</span>
                      </div>
                      {teams?.[1]?.player2 && (
                        <div
                          className={`flex gap-x-4 items-center border flex-1 rounded p-4 bg-gray-300 ${
                            currentWinner === 'team2' ? 'border-green-400' : 'border-gray-400'
                          }`}
                        >
                          {teams?.[1]?.player2?.imageUrl ? (
                            <img src={teams?.[1]?.player2?.imageUrl} className="w-6 h-6 rounded-full" alt="" />
                          ) : (
                            <UserIconFilled className="w-6 h-6 text-primary-500" />
                          )}
                          <span className={`text-sm font-medium text-navy-900`}>{teams?.[1]?.player2?.fullName}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-center gap-x-6">
                  <ButtonPrimary
                    title={t('common.submit')}
                    className="!py-[6px] !px-4 !rounded"
                    onClick={handleSubmit(onSubmit)}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
        <ConfirmAddEditScoreModal
          title={t('event.titleSubmitScoreModal')}
          submitTitle={t('common.submit')}
          open={showWarningPopup}
          onClose={() => setShowWarningPopup((pre) => !pre)}
          onSubmit={() => handleEditedScore(false)}
          content={
            <div className="px-6">
              <p className=" text-navy-900 text-opacity-50 text-sm text-center">{t('event.scoreWarningMessage')}</p>
            </div>
          }
        />
        <ConfirmAddEditScoreModal
          title={t('event.editingScoresTitle')}
          submitTitle={t('event.submitScores')}
          open={showCompletedErrorPopup}
          onClose={() => setCompletedErrorPopup((pre) => !pre)}
          onSubmit={() => handleEditedScore(true)}
          content={
            <div className="px-6">
              <p className=" text-navy-900 text-opacity-50 text-sm text-center">
                {t('event.editingScoresFirstDesc')}
                <span className="font-medium text-boldOrange">{t('event.editingScoresSecondDesc')}</span>
                {t('event.stillContinue')}
              </p>
            </div>
          }
        />
        <ConfirmAddEditScoreModal
          title={t('event.submittingScoresTitle')}
          submitTitle={t('event.submitScores')}
          open={showOngoingErrorPopup}
          onClose={() => setOngoingErrorPopup((pre) => !pre)}
          onSubmit={() => handleEditedScore(true)}
          content={
            <div className="px-6">
              <p className=" text-navy-900 text-sm text-center">
                <span className="font-medium">{t('event.submittingScoresFirstDesc')}</span>
                <span className="text-navy-900 text-opacity-50">{t('event.submittingScoresSecondDesc')}</span>
              </p>
            </div>
          }
        />
      </Dialog>
    </Transition>
  )
}
